import styled from 'styled-components'
import RaisedButton from 'components/RaisedButton/RaisedButton'
import { getThemepalette } from 'config/theme'

const lightContentTheme = getThemepalette('light')

type LinkedReferenceSectionProps = {
  pageNumber?: number
  sectionLabel?: string
  referenceSectionTitle?: string | null
  readOnly?: boolean
  onViewSection?: () => void
  disableViewSection?: boolean
}

const LinkedReferenceSection = ({
  pageNumber = 1,
  sectionLabel = 'Nonfiction',
  referenceSectionTitle = null,
  onViewSection,
  disableViewSection = true
}: LinkedReferenceSectionProps) => {
  return (
    <Container className='contrast-area'>
      <PageNumberBadge>{pageNumber}</PageNumberBadge>
      <Content>
        <Instruction>
          {referenceSectionTitle === null ? (
            <>
              Refer to the <SectionName>{sectionLabel}</SectionName> section to
              answer the following questions.
            </>
          ) : (
            referenceSectionTitle
          )}
        </Instruction>
      </Content>
      <ViewSectionButton
        onClick={disableViewSection ? undefined : onViewSection}
        $disabled={disableViewSection}
      >
        <ViewSectionButtonText>View Section</ViewSectionButtonText>
      </ViewSectionButton>
    </Container>
  )
}

const Container = styled.section`
  display: flex;
  align-items: center;
  min-width: 720px;
  max-width: 1000px;
  height: 45px;
  gap: 10px;
  border: 1px solid ${lightContentTheme.colors.border.primary};
  border-radius: 8px;
  background: ${lightContentTheme.colors.card.background};
  padding: 8px 7px 8px 15px;
  margin-bottom: 20px;
  margin-top: 20px;
`

const PageNumberBadge = styled(RaisedButton)`
  /**
   * RaisedButton owns its own theme-aware shell colors, so pin the linked
   * section badge to the fixed light content palette explicitly.
   */
  background-color: ${lightContentTheme.colors.card.background} !important;
  border: 1px solid ${lightContentTheme.colors.border.primary} !important;
  filter: drop-shadow(
    1px 1px 0 ${lightContentTheme.colors.border.primary}
  ) !important;
  width: 22px;
  height: 29px;
  min-width: 22px;
  border-radius: 0;
  padding: 0;
  cursor: default;
  justify-content: center;
  color: ${lightContentTheme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontInter};
  font-size: 16px;
  line-height: 1;
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`

const Instruction = styled.p`
  margin: 0;
  color: ${lightContentTheme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunitoSans};
  font-size: 16px;
  line-height: 1.3;
`

const SectionName = styled.strong`
  font-family: ${(props) => props.theme.typography.fontNunitoSans};
  font-size: 16px;
  font-weight: 800;
`

const ViewSectionButton = styled(RaisedButton)<{ $disabled?: boolean }>`
  /**
   * Keep the linked reference CTA on the same fixed light shell while
   * preserving the current disabled interaction state.
   */
  background-color: ${lightContentTheme.colors.card.background} !important;
  border: 1px solid ${lightContentTheme.colors.border.primary} !important;
  filter: drop-shadow(
    1px 1px 0 ${lightContentTheme.colors.border.primary}
  ) !important;
  width: 124px;
  height: 33px;
  justify-content: center;
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
`

const ViewSectionButtonText = styled.span`
  color: ${lightContentTheme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 14px;
  /* font-weight: 700; */
`

export default LinkedReferenceSection
